<template>
    <transition appear name="fade" mode="out-in">
        <app-lacuna
            v-if="loading"
            icon="activity"
            :title="$t('activity')"
            :icon-size="dense ? '3rem' : null"
            :style="style"
        >
            <template #text>
                <text-animation-loader :loading="loading" :prepend="$t('loading')" spinner class="pl-1" />
            </template>
        </app-lacuna>

        <component
            :is="noScroll ? 'AppWrapper' : 'AppScrollbar'"
            v-else-if="filteredEvents && filteredEvents.length"
            :class="noScroll ? 'pt-4' : ''"
            style="height: 100%"
        >
            <v-timeline :class="classList" :dense="dense" align-top :style="style" class="pt-0">
                <app-timeline-item
                    v-for="(item, index) in sortedEvents"
                    :key="index"
                    :event="item"
                    :readonly="readonly"
                    :editable="editable || item.editable"
                    :awaiting-todo-save="
                        updatingTodoItems && todoItems.length && todoItems.find(i => i.id === item.id) ? true : false
                    "
                    @input:todo="updateTodo"
                    @saved="$emit('saved', $event)"
                    @cancel-editing="$emit('cancel-editing')"
                    @click:alert="$emit('click:alert', $event)"
                />
            </v-timeline>
        </component>

        <app-lacuna
            v-else
            icon="activity"
            :title="$t('activity')"
            :text="$t('we_couldnt_find_any_activity')"
            :icon-size="dense ? '3rem' : null"
            :style="style"
        />
    </transition>
</template>

<script>
import clock from '@/lib/clock';

export default {
    name: 'AppTimeline',
    components: {
        AppTimelineItem: () =>
            import(
                /* webpackChunkName: "core" */
                '@/components/AppTimelineItem'
            )
    },
    props: {
        title: {
            type: String,
            default: null
        },
        dense: {
            type: Boolean,
            default: false
        },
        small: {
            type: Boolean,
            default: false
        },
        maxHeight: {
            type: [String, Number],
            default: null
        },
        minHeight: {
            type: [String, Number],
            default: null
        },
        height: {
            type: [String, Number],
            default: null
        },
        events: {
            type: Array,
            default: () => []
        },
        noScroll: {
            type: Boolean,
            default: false
        },
        asc: {
            type: Boolean,
            default: false
        },
        desc: {
            type: Boolean,
            default: false
        },
        sortKey: {
            type: String,
            default: 'created_at'
        },
        readonly: {
            type: Boolean,
            default: false
        },
        editable: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        todoItems: [],
        updatingTodoItems: false,
        debounceEmitEvents: () => {}
    }),
    computed: {
        classList() {
            let classList = ['app-timeline'];

            if (this.small) {
                classList.push('app-timeline--small');
            }

            return classList.join(' ');
        },
        style() {
            let style = [];

            if (this.maxHeight !== null && this.maxHeight !== undefined) {
                if (this._.isNumber(this.maxHeight)) {
                    style.push(`max-height: ${this.maxHeight}px`);
                } else {
                    style.push(`max-height: ${this.maxHeight}`);
                }
            }

            if (this.minHeight !== null && this.minHeight !== undefined) {
                if (this._.isNumber(this.minHeight)) {
                    style.push(`min-height: ${this.minHeight}px`);
                } else {
                    style.push(`min-height: ${this.minHeight}`);
                }
            }

            if (this.height !== null && this.height !== undefined) {
                if (this._.isNumber(this.height)) {
                    style.push(`height: ${this.height}px`);
                } else {
                    style.push(`height: ${this.height}`);
                }
            }

            return style.join(';');
        },
        filteredEvents() {
            const isGI = this.isGrowthInvest();

            return this.events.filter(event => {
                if (!isGI && 'todo' in event && typeof event.todo === 'boolean' && event.todo) {
                    return false;
                }
                return true;
            });
        },
        sortedEvents() {
            if (this.asc) {
                return this.eventsSortedAsc;
            } else if (this.desc) {
                return this.eventsSortedDesc;
            }

            return this.filteredEvents;
        },
        eventsSortedAsc() {
            const events = this.filteredEvents;

            events.sort((a, b) => {
                if (!a[`${this.sortKey}`] && !b[`${this.sortKey}`]) {
                    return 0;
                } else if (!a[`${this.sortKey}`]) {
                    return 1;
                } else if (!b[`${this.sortKey}`]) {
                    return -1;
                }
                const createdAtA = clock(a[`${this.sortKey}`]);
                const createdAtB = clock(b[`${this.sortKey}`]);
                return createdAtA.diff(createdAtB);
            });

            return events;
        },
        eventsSortedDesc() {
            const events = this.filteredEvents;

            events.sort((a, b) => {
                if (!a[`${this.sortKey}`] && !b[`${this.sortKey}`]) {
                    return 0;
                } else if (!a[`${this.sortKey}`]) {
                    return -1;
                } else if (!b[`${this.sortKey}`]) {
                    return 1;
                }
                const createdAtA = clock(a[`${this.sortKey}`]);
                const createdAtB = clock(b[`${this.sortKey}`]);
                return createdAtB.diff(createdAtA);
            });

            return events;
        }
    },
    mounted() {
        this.debounceEmitEvents = this._.debounce(this.emitEvents, 2000);
    },
    methods: {
        updateTodo(item) {
            this.todoItems.push(item);
            this.updatingTodoItems = true;
            this.debounceEmitEvents();
        },
        emitEvents() {
            const updatedEventIds = [];

            const events = this.events.map(event => {
                if (this.todoItems.length) {
                    const updatedTodoItem = this.todoItems.find(item => item.id === event.id);

                    if (updatedTodoItem) {
                        updatedEventIds.push(updatedTodoItem.id);
                        return updatedTodoItem;
                    }
                }

                return event;
            });

            this.$emit('input:items', events, updatedEventIds);
            this.todoItems = [];
            this.updatingTodoItems = false;
        }
    }
};
</script>
